import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { RoleEnum } from '@core/services/role/role.model';
import { environment } from '@env/environment';
import { ErrorCodeComponent } from '@shared/components/error-code/error-code.component';
import { AuthGuardService } from '../core/services/guards/auth-guard.service';
import { LayoutComponent } from '../theme/layout/layout.component';
import { AccountViewComponent } from './account-view/account-view.component';
import { AccountViewGuard } from './account-view/account-view.guard';

import { AdminReportComponent } from './admin-report/admin-report.component';
import { LoginComponent } from './auth/Login/login.component';
import { TermsAndConditionsComponent } from './auth/terms-and-conditions/terms-and-conditions.component';
import { UserManualComponent } from './auth/user-manual/user-manual.component';
import { BusinessReportComponent } from './business-report/business-report.component';
import { ClientComponent } from './client/client.component';
import { ClientStepperComponent } from './client/components/client-stepper/client-stepper.component';
import { DownloadInputFilesComponent } from './download-input-files/download-input-files.component';
import { DownloadReportComponent } from './download-report/download-report.component';
import { GoToLoginComponent } from './go-to-login/go-to-login.component';
import { HangfireDashboardComponent } from './hangfire/components/hangfire-dashboard/hangfire-dashboard.component';
import { JobManagerComponent } from './hangfire/components/job-manager/job-manager.component';
import { IpFilteringComponent } from './ip-filtering/ip-filtering.component';
import { LandingPageComponent } from './landing-page/landing-page.component';
import { LookupPageComponent } from './lookup-page/lookup-page.component';
import { ForgetPasswordComponent } from './password-security/forget-password/forget-password.component';
import { PasswordRecoveryQuestionComponent } from './password-security/password-recovery-question/password-recovery-question.component';
import { PasswordSecurityComponent } from './password-security/password-security.component';
import { ResetPasswordSecurityComponent } from './password-security/reset-password-security/reset-password-security.component';
import { ReportDashboardComponent } from './report-dashboard/report-dashboard.component';
import { RoleManagementComponent } from './role-management/role-management.component';
import { RoleComponent } from './role-management/role/role.component';
import { SftpComponent } from './sftp/sftp.component';
import { HolidayDateComponent } from './system-setup/components/holiday-date/holiday-date.component';
import { HolidayComponent } from './system-setup/components/holiday/holiday.component';
import { UserEditComponent } from './user/components/user-edit/user-edit.component';
import { UserPasswordChangeComponent } from './user/components/user-password-change/user-password-change.component';
import { UserProfileComponent } from './user/components/user-profile/user-profile.component';
import { UserComponent } from './user/user.component';
import { WorkQueueComponent } from './work-queue/work-queue.component';

const routes: Routes = [
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'accountview',
        component: AccountViewComponent,
        canActivate: [AuthGuardService],
        canDeactivate: [AccountViewGuard],
        data: {
          title: 'Account View',
          titleI18n: 'accountview',
          roles: [
            RoleEnum.administrator,
            RoleEnum.businessInternalUser,
            RoleEnum.supportUser,
            RoleEnum.clientAdministrator,
            RoleEnum.clientUser,
          ],
        },
      },
      {
        path: 'workqueue',
        component: WorkQueueComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Work Queue',
          titleI18n: 'workqueue',
          roles: [
            RoleEnum.administrator,
            RoleEnum.businessInternalUser,
            RoleEnum.clientAdministrator,
            RoleEnum.clientUser,
          ],
        },
      },
      {
        path: 'dashboard',
        component: LandingPageComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Dashboard',
          titleI18n: 'dashboard',
          roles: [
            RoleEnum.administrator,
            RoleEnum.businessInternalUser,
            RoleEnum.supportUser,
            RoleEnum.clientAdministrator,
            RoleEnum.clientUser,
            RoleEnum.dealerCredit,
          ],
        },
      },
      {
        path: 'businessreport',
        component: BusinessReportComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Business Report',
          titleI18n: 'Business Report',
          roles: [
            RoleEnum.administrator,
            RoleEnum.businessInternalUser,
            RoleEnum.supportUser,
            RoleEnum.clientAdministrator,
            RoleEnum.clientUser,
          ],
        },
      },
      {
        path: 'lookup',
        component: LookupPageComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Lookup',
          titleI18n: 'Lookup',
          roles: [
            RoleEnum.administrator,
            RoleEnum.businessInternalUser,
            RoleEnum.clientAdministrator,
            RoleEnum.clientUser,
          ],
        },
      },
      {
        path: 'downloadreport',
        component: DownloadReportComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'downloadreport',
          titleI18n: 'downloadreport',
          roles: [
            RoleEnum.administrator,
            RoleEnum.businessInternalUser,
            RoleEnum.clientAdministrator,
            RoleEnum.clientUser,
          ],
        },
      },
      {
        path: 'user',
        component: UserComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Manage User',
          titleI18n: 'Manage User',
          roles: [RoleEnum.administrator, RoleEnum.clientAdministrator],
        },
      },
      {
        path: 'edituser',
        component: UserEditComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Manage User',
          titleI18n: 'Manage User',
          roles: [RoleEnum.administrator, RoleEnum.clientAdministrator],
        },
      },
      {
        path: 'edituser/:id',
        component: UserEditComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Manage User',
          titleI18n: 'Manage User',
          roles: [RoleEnum.administrator, RoleEnum.clientAdministrator],
        },
      },
      {
        path: 'hfdashboard',
        component: HangfireDashboardComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Hangfire Dashboard',
          titleI18n: 'Hangfire Dashboard',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'jobmanager',
        component: JobManagerComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Job Manager',
          titleI18n: 'Job Manager',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'reportdashboard',
        component: ReportDashboardComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Report',
          titleI18n: 'Report',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'businessreport',
        component: BusinessReportComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'businessreport',
          titleI18n: 'Report',
          roles: [RoleEnum.administrator, RoleEnum.businessInternalUser, RoleEnum.supportUser],
        },
      },
      {
        path: 'administratorreport',
        component: AdminReportComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'businessreport',
          titleI18n: 'Report',
          roles: [RoleEnum.administrator, RoleEnum.businessInternalUser, RoleEnum.supportUser],
        },
      },
      {
        path: 'clientinfo',
        component: ClientComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Client List',
          titleI18n: 'Client List',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'editclient',
        component: ClientStepperComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Edit Client',
          titleI18n: 'Edit Client',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'downloadinputfile',
        component: DownloadInputFilesComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Download Input File',
          titleI18n: 'downloadinputfile',
          roles: [RoleEnum.administrator, RoleEnum.businessInternalUser, RoleEnum.supportUser],
        },
      },
      {
        path: 'holiday',
        component: HolidayComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'Holiday',
          titleI18n: 'Holiday',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'holidaydate',
        component: HolidayDateComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'HolidayDate',
          titleI18n: 'HolidayDate',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'sftp',
        component: SftpComponent,
        canActivate: [AuthGuardService],
        data: {
          title: 'SFTP',
          titleI18n: 'SFTP',
          roles: [RoleEnum.administrator, RoleEnum.supportUser],
        },
      },
      {
        path: 'updatepassword',
        component: UserPasswordChangeComponent,
        data: {
          title: 'Update Password',
          titleI18n: 'Update Password',
        },
      },
      {
        path: 'userprofile',
        component: UserProfileComponent,
        data: {
          title: 'User Profile',
          titleI18n: 'User Profile',
        },
      },
      {
        path: 'passwordsecurity',
        component: PasswordSecurityComponent,
        data: {
          title: 'Password Security',
          titleI18n: 'Password Security',
        },
      },
      {
        path: 'vfp',
        loadChildren: () => import('./vfp/vfp.module').then(m => m.VfpModule),
        canActivate: [AuthGuardService],
        data: {
          title: 'Virtual Floor Plan',
          titleI18n: 'Virtual Floor Plan',
          roles: [RoleEnum.administrator, RoleEnum.businessInternalUser, RoleEnum.supportUser],
        },
      },
      {
        path: 'fileprocessing',
        loadChildren: () =>
          import('./file-processing/file-processing.module').then(m => m.FileProcessingModule),
        canActivate: [AuthGuardService],
        data: {
          title: 'File Processing',
          titleI18n: 'File Processing',
          roles: [
            RoleEnum.administrator,
            RoleEnum.businessInternalUser,
            RoleEnum.supportUser,
            RoleEnum.dealerCredit,
          ],
        },
      },
      {
        path: 'termandcondition',
        component: TermsAndConditionsComponent,
        data: {
          title: 'Term and Condition',
          titleI18n: 'Term and Condition',
        },
      },
      {
        path: 'usermanual',
        component: UserManualComponent,
        data: {
          title: 'User Manual',
          titleI18n: 'User Manual',
        },
      },
      {
        path: 'gotologin',
        component: GoToLoginComponent,
        data: {
          title: 'Go To Login',
          titleI18n: 'Go To Login',
        },
      },
      {
        path: 'ipfiltering',
        component: IpFilteringComponent,
        data: {
          title: 'IP Filtering',
          titleI18n: 'IP Filtering',
        },
      },
      {
        path: 'roles',
        component: RoleManagementComponent,
        data: { title: 'Roles', titleI18n: 'Roles' },
      },
      {
        path: 'role',
        component: RoleComponent,
        data: { title: 'Role', titleI18n: 'Role' },
      },
      
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full',
      },
      {
        path: 'login',
        component: LoginComponent,
        data: {
          title: 'Login',
          titleI18n: 'Login',
        },
      },
      {
        path: 'error',
        component: ErrorCodeComponent,
        data: {
          title: 'error',
          titleI18n: 'error',
        },
      },
      {
        path: 'passwordsecurity',
        component: PasswordSecurityComponent,
        data: {
          title: 'Password Security',
          titleI18n: 'Password Security',
        },
      },
      {
        path: 'forgetpassword',
        component: ForgetPasswordComponent,
        data: {
          title: 'Forget Password',
          titleI18n: 'Forget Password',
        },
      },
      {
        path: 'resetpassword',
        component: ResetPasswordSecurityComponent,
        data: {
          title: 'Reset Password',
          titleI18n: 'Reset Password',
        },
      },
      {
        path: 'passwordrecoveryquestion',
        component: PasswordRecoveryQuestionComponent,
        data: {
          title: 'Password Recovery Question',
          titleI18n: 'Password Recovery Question',
        },
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: environment.useHash,
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      enableTracing: false,
    }),
  ],
  exports: [RouterModule],
})
export class FeaturesRoutingModule { }
